// The worklist's filters, as the address carries them (`RPM-004`, `CR-44`, `J-535`).
//
// "The filters are kept in the address, so a reload, a session lock or a bookmark returns to the same
// list" (the Keyboard row). Seven of the eight are: a building's mirror code, care-unit UUIDs, and
// words from closed lists. The eighth, Resident, is part of a name, and a name never goes in an
// address (`.claude/rules/phi.md` rule 2, `D-1161`): it is matched in the browser over the rows
// loaded, and held in memory only (`use-resident-filter.ts`).
//
// EVERY FIELD STARTS EMPTY AND A BAD ONE IS DROPPED. An address edited by hand or bookmarked before a
// value was retired reads as that filter not set, never as an error page (`.catch(undefined)`).
//
// PURE, IN ITS OWN FILE: a helper exported beside a component fails `react-refresh` at
// `--max-warnings 0`, and the route and the list both read these.
import { z } from "zod";
import type { paths } from "../../api/schema";

type EntriesQuery = NonNullable<paths["/api/rpm/v1/entries"]["get"]["parameters"]["query"]>;

/** The lanes the Classification control offers, worst first, in a nurse's words. */
export const LANE_CHOICES = [
  { value: "CRITICAL", label: "Critical" },
  { value: "CONCERNING", label: "Concerning" },
  { value: "CLEAR", label: "Clear" },
  { value: "MACHINE", label: "Machine error" },
] as const satisfies readonly { value: NonNullable<EntriesQuery["lane"]>; label: string }[];

/** The contact methods a step can owe, as the Contact method owed control offers them. */
export const METHOD_CHOICES = [
  { value: "CALL", label: "Call" },
  { value: "EMAIL", label: "Email" },
  { value: "TEXT", label: "Text" },
  { value: "DAILY_SUMMARY", label: "Daily summary" },
] as const satisfies readonly {
  value: NonNullable<EntriesQuery["contactMethod"]>;
  label: string;
}[];

/** "Waiting longer than — 30 minutes, 1 hour, 4 hours or any" (the Operator surface row). */
export const WAIT_CHOICES = [
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "4h", label: "4 hours" },
] as const satisfies readonly {
  value: NonNullable<EntriesQuery["waitingLongerThan"]>;
  label: string;
}[];

const oneOf = <T extends readonly { value: string }[]>(choices: T) =>
  z
    .enum(choices.map((c) => c.value) as [T[number]["value"], ...T[number]["value"][]])
    .optional()
    .catch(undefined);

const day = z.iso.date().optional().catch(undefined);

/** The worklist's address. Ids, words from closed lists and dates only; never a name. */
export const worklistSearchSchema = z.object({
  facilityId: z.string().min(1).max(20).optional().catch(undefined),
  careUnitIds: z.array(z.uuid()).min(1).max(100).optional().catch(undefined),
  enteredBy: z.literal("me").optional().catch(undefined),
  lane: oneOf(LANE_CHOICES),
  contactMethod: oneOf(METHOD_CHOICES),
  takenFrom: day,
  takenTo: day,
  waitingLongerThan: oneOf(WAIT_CHOICES),
  /** The entry last opened from here, so Back to worklist returns focus to its row. */
  opened: z.uuid().optional().catch(undefined),
});
type WorklistSearch = z.infer<typeof worklistSearchSchema>;

/** The filters alone: the address without the row last opened. */
export type WorklistFilters = Omit<WorklistSearch, "opened">;

/**
 * The read the filters ask for. A care-unit filter without a building is dropped rather than sent:
 * the route refuses it (`careUnitIds` needs `facilityId`), and the building control clears the units
 * whenever it changes, so only a hand-edited address reaches this.
 */
export function entriesQueryOf(f: WorklistFilters): EntriesQuery {
  const units = f.facilityId === undefined ? undefined : f.careUnitIds;
  // A range entered backwards is read the right way round rather than refused.
  const [from, to] =
    f.takenFrom !== undefined && f.takenTo !== undefined && f.takenFrom > f.takenTo
      ? [f.takenTo, f.takenFrom]
      : [f.takenFrom, f.takenTo];
  return {
    ...(f.facilityId === undefined ? {} : { facilityId: f.facilityId }),
    ...(units === undefined ? {} : { careUnitIds: units.join(",") }),
    ...(f.enteredBy === undefined ? {} : { enteredBy: f.enteredBy }),
    ...(f.lane === undefined ? {} : { lane: f.lane }),
    ...(f.contactMethod === undefined ? {} : { contactMethod: f.contactMethod }),
    ...(from === undefined ? {} : { takenFrom: from }),
    ...(to === undefined ? {} : { takenTo: to }),
    ...(f.waitingLongerThan === undefined ? {} : { waitingLongerThan: f.waitingLongerThan }),
  };
}

/** Whether any filter kept in the address is set. */
export function anyFilterSet(f: WorklistFilters): boolean {
  return Object.values(entriesQueryOf(f)).some((v) => v !== undefined);
}

/**
 * A FILTER CHANGE IS MERGED INTO THE ADDRESS AS IT IS WHEN WRITTEN, not into the one the screen last
 * rendered. Each control builds its change from the filters it was drawn with, so two quick changes
 * — a building, then Critical — both carried the old address, and the second dropped the first
 * (`J-228`'s CI run, `worklist.spec.ts:312`). Only the keys a change alters are applied; the row
 * last opened is dropped, as any filter change has always dropped it.
 */
export function mergeFilterChange(
  current: WorklistSearch,
  drawnWith: WorklistFilters,
  next: WorklistFilters,
): WorklistFilters {
  const keys = new Set([...Object.keys(drawnWith), ...Object.keys(next)]) as Set<
    keyof WorklistFilters
  >;
  const merged: Record<string, unknown> = { ...current };
  delete merged.opened;
  for (const key of keys) {
    if (JSON.stringify(drawnWith[key]) !== JSON.stringify(next[key])) merged[key] = next[key];
  }
  return merged;
}
